import { gql, TypedDocumentNode } from "@apollo/client";

export interface CreateStudentInternshipInput {
  startedOn: string;
  endedOn?: string | null;
  isPrimary?: boolean;
  workingLocation: "ON_SITE" | "REMOTE" | "HYBRID";
}

export interface UpdateStudentInternshipInput {
  startedOn?: string;
  endedOn?: string | null;
  isPrimary?: boolean;
  workingLocation?: "ON_SITE" | "REMOTE" | "HYBRID";
}

export interface BackofficeEducationInternship {
  id: string;
  title: string;
  jobClassification: string;
  startedOn: string;
  endedOn: string;
  workingLocation: string;
  primary: boolean;
  description: string;
  internshipCredits: number;
  durationMonths: number;
  isActive: boolean;
  internshipType: string;
  universitySectionName: string;
  employmentSectionName: string;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    pgaId: string;
    email: string;
  };
  facility: {
    id: string;
    name: string;
  };
}

export interface BackofficeEducationStudentInternshipResponse {
  code: number;
  internship: BackofficeEducationInternship;
  message: string;
  success: boolean;
}

export interface CreateStudentInternshipMutation {
  createStudentInternship: BackofficeEducationStudentInternshipResponse;
}

export interface UpdateStudentInternshipMutation {
  updateStudentInternship: BackofficeEducationStudentInternshipResponse;
}

export const CREATE_STUDENT_INTERNSHIP_MUTATION: TypedDocumentNode<
  CreateStudentInternshipMutation,
  { studentId: string; facilityId: string; supervisorId: string; input: CreateStudentInternshipInput }
> = gql`
  mutation createStudentInternship(
    $studentId: ID!
    $facilityId: ID!
    $supervisorId: ID!
    $input: BackofficeEducationCreateStudentInternshipInput!
  ) {
    createStudentInternship(
      studentId: $studentId
      facilityId: $facilityId
      supervisorId: $supervisorId
      input: $input
    ) {
      code
      success
      message
      internship {
        id
        title
        jobClassification
        startedOn
        endedOn
        workingLocation
        isPrimary
        internshipCredits
        durationMonths
        isActive
        internshipType
        universitySectionName
        employmentSectionName
        person {
          id
          firstName
          lastName
          pgaId
          email
        }
        facility {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_STUDENT_INTERNSHIP_MUTATION: TypedDocumentNode<
  UpdateStudentInternshipMutation,
  { employmentId: string; facilityId?: string; supervisorId?: string; input: UpdateStudentInternshipInput }
> = gql`
  mutation updateStudentInternship(
    $employmentId: ID!
    $facilityId: ID!
    $supervisorId: ID!
    $input: BackofficeEducationUpdateStudentInternshipInput!
  ) {
    updateStudentInternship(
      employmentId: $employmentId
      facilityId: $facilityId
      supervisorId: $supervisorId
      input: $input
    ) {
      code
      success
      message
      internship {
        id
        title
        jobClassification
        startedOn
        endedOn
        workingLocation
        isPrimary
        internshipCredits
        durationMonths
        isActive
        internshipType
        universitySectionName
        employmentSectionName
        person {
          id
          firstName
          lastName
          pgaId
          email
        }
        facility {
          id
          name
        }
      }
    }
  }
`;

export interface DeleteStudentInternshipMutation {
  deleteEmployment: {
    success: boolean;
    message: string;
    employment: { person: { id: string; __typename: string } };
  };
}

export const DELETE_STUDENT_INTERNSHIP_MUTATION: TypedDocumentNode<DeleteStudentInternshipMutation, { id: string }> =
  gql`
    mutation DeleteStudentInternship($id: ID!) {
      deleteEmployment(id: $id) {
        success
        message
        employment {
          person {
            id
          }
        }
      }
    }
  `;
